import type DocRootMissingModal from "@components/Layouts/CatalogLayout/DocRootMissingModal";
import RightNavigationComponent from "@components/Layouts/CatalogLayout/RightNavigation/RightNavigationComponent";
import type { ArticlePageData } from "@core/SitePresenter/types/ArticlePage";
import ModalToOpenService from "@core-ui/ContextServices/ModalToOpenService/ModalToOpenService";
import ModalToOpen from "@core-ui/ContextServices/ModalToOpenService/model/ModalsToOpen";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import SidebarsIsOpenService from "@core-ui/ContextServices/Sidebars/SidebarsIsOpenContext";
import useArticleScrollPosition from "@core-ui/hooks/useArticleScrollPosition";
import useMediaQuery from "@core-ui/hooks/useMediaQuery";
import { cssMedia } from "@core-ui/utils/cssUtils";
import ChatPanelOverlay from "@ext/agent/components/panel/ChatPanelOverlay";
import { feature } from "@ext/toggleFeatures/features";
import { type ComponentProps, useEffect } from "react";
import ArticleComponent from "./ArticleLayout/ArticleComponent";
import CatalogLayout from "./CatalogLayout";
import LeftNavigationComponent from "./LeftNavigation/LeftNavigationComponent";
import LeftNavigationNarrowComponent from "./LeftNavigation/Narrow/LeftNavigationNarrowComponent";

const OPEN_DELAY_MS = 50;

const CatalogComponent = ({ data, children }: { data: ArticlePageData; children: JSX.Element }) => {
	const narrowMedia = useMediaQuery(cssMedia.JSnarrow);
	const mediumMedia = useMediaQuery(cssMedia.JSmedium);
	const pageDataContext = PageDataContextService.value;
	const isReadOnly = pageDataContext.conf.isReadOnly;

	// Lives here, so it covers every article surface — the docportal/browser
	useArticleScrollPosition(data);

	// biome-ignore lint/correctness/useExhaustiveDependencies: expected
	useEffect(() => {
		if (isReadOnly || !data.catalogProps.docrootIsNoneExsistent) return;
		ModalToOpenService.setValue<ComponentProps<typeof DocRootMissingModal>>(ModalToOpen.DocRootMissingModal, {
			onClose: () => ModalToOpenService.resetValue(),
		});
	}, []);

	return (
		<SidebarsIsOpenService.Provider>
			<>
				<CatalogLayout
					article={
						<ArticleComponent
							article={children}
							rightNav={<RightNavigationComponent delay={OPEN_DELAY_MS} />}
						/>
					}
					catalogNav={
						narrowMedia ? (
							<LeftNavigationNarrowComponent data={data} />
						) : (
							<LeftNavigationComponent data={data} delay={OPEN_DELAY_MS} mediumMedia={mediumMedia} />
						)
					}
				/>
				{feature("agent-chat") && <ChatPanelOverlay />}
			</>
		</SidebarsIsOpenService.Provider>
	);
};

export default CatalogComponent;
